import { useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SeoHead from '@/components/base/SeoHead';
import { tvShows } from '@/mocks/tvShows';

const MOCK_EPISODES_COUNT: Record<number, number> = {
  1: 40, 2: 39, 3: 16, 4: 36, 5: 16, 6: 73, 7: 40, 8: 34, 9: 10, 10: 39,
  11: 16, 12: 62, 13: 30, 14: 87, 15: 15, 16: 47,
};

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const showId = parseInt(id || '1', 10);
  const show = useMemo(() => tvShows.find(s => s.id === showId), [showId]);

  const relatedShows = useMemo(() => {
    if (!show) return [];
    return tvShows
      .filter(s => s.id !== show.id && (s.categorySlug === show.categorySlug || s.tags.some(t => show.tags.includes(t))))
      .slice(0, 8);
  }, [show]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [showId]);

  if (!show) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-800 mb-2">未找到该影视</h2>
            <p className="text-foreground-400 text-sm mb-4">该影视资源可能已下架或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 font-medium text-sm rounded-xl hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const totalEpisodes = MOCK_EPISODES_COUNT[show.id] || show.episodes;
  const episodeNumbers = Array.from({ length: Math.min(totalEpisodes, 40) }, (_, i) => i + 1);
  const currentEpNum = show.status === 'completed' ? totalEpisodes : show.currentEpisode;

  const seoTitle = `${show.name}全集在线观看 - ${show.name}免费高清不卡顿 - 97影院`;
  const seoDesc = `97影院提供${show.name}全集在线观看，${show.name}剧情简介：${show.description.slice(0, 100)}... 高清蓝光不卡顿流畅播放。主演：${show.actors.slice(0, 5).join('、')}。`;
  const seoKeywords = `${show.name},${show.name}在线观看,${show.name}全集,${show.name}免费,${show.category},${show.tags.slice(0, 3).join(',')},97影院`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: show.name,
    description: show.description,
    image: show.poster,
    dateCreated: `${show.year}-01-01`,
    director: { '@type': 'Person', name: show.director },
    actor: show.actors.map(a => ({ '@type': 'Person', name: a })),
    countryOfOrigin: { '@type': 'Country', name: show.region },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: show.rating,
      bestRating: '10',
      ratingCount: Math.floor(parseFloat(show.totalViews) * 100),
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${show.name}在哪里可以免费在线观看？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${show.name}可以在97影院免费在线观看。97影院提供高清蓝光画质，流畅不卡顿的观影体验。`,
        },
      },
      {
        '@type': 'Question',

        name: `${show.name}一共有多少集？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${show.name}共${totalEpisodes}集，${show.status === 'completed' ? '已完结' : `更新至${show.currentEpisode}集`}。`,
        },
      },
      {
        '@type': 'Question',
        name: `${show.name}的主演是谁？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${show.name}由${show.actors.slice(0, 5).join('、')}等主演，导演是${show.director}。`,
        },
      },
    ],
  };

  return (
    <>
      <SeoHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl={`https://97yingyuan.com/detail/${show.id}`}
        ogImage={show.poster}
        ogType="video.movie"
        schemaData={articleSchema}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema, null, 2) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="w-full h-[300px] md:h-[420px] relative overflow-hidden">
            <img
              src={show.poster}
              alt={show.name}
              className="w-full h-full object-cover object-top blur-xl opacity-30 scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background-50/60 to-background-50"></div>
          </div>

          <div className="w-full px-4 md:px-6 -mt-[260px] md:-mt-[360px] relative z-10">
            <div className="max-w-7xl mx-auto">
              <div className="mb-4">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: show.category, href: `/category/${show.categorySlug}` },
                    { label: show.name },
                  ]}
                />
              </div>

              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="w-40 md:w-64 flex-shrink-0 mx-auto md:mx-0">
                  <div className="rounded-xl overflow-hidden shadow-2xl">
                    <img
                      src={show.poster}
                      alt={`${show.name}海报`}
                      title={`${show.name} ${show.tags.join(' ')}`}
                      className="w-full aspect-[3/4] object-cover"
                    />
                  </div>
                </div>

                <div className="flex-1 pt-2">
                  <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
                    {show.name}
                  </h1>
                  <p className="text-foreground-400 text-sm mb-1">{show.englishName}</p>

                  <div className="flex items-center gap-3 flex-wrap mt-3 mb-4">
                    <span className="px-2.5 py-1 bg-primary-500/20 text-primary-500 text-xs font-medium rounded-lg whitespace-nowrap">
                      {show.rating}分
                    </span>
                    <span className="text-foreground-500 text-sm">{show.year}</span>
                    <span className="text-foreground-500 text-sm">{show.category}</span>
                    <span className="text-foreground-500 text-sm">{show.region}</span>
                    {show.status === 'updating' ? (
                      <span className="px-2 py-0.5 bg-accent-500/20 text-accent-500 text-xs font-medium rounded-md whitespace-nowrap">
                        更新至{show.currentEpisode}集
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-secondary-500/20 text-secondary-500 text-xs font-medium rounded-md whitespace-nowrap">
                        已完结
                      </span>
                    )}
                    <span className="text-foreground-400 text-xs">{show.totalViews}次播放</span>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap mb-5">
                    {show.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 bg-background-100 text-foreground-500 text-xs rounded-md whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className="text-foreground-500 text-sm leading-relaxed mb-5">
                    <strong className="text-foreground-700">剧情简介：</strong>
                    {show.description}
                  </p>

                  <div className="grid grid-cols-2 gap-2 text-sm mb-6">
                    <div>
                      <span className="text-foreground-400">导演：</span>
                      <span className="text-foreground-700">{show.director}</span>
                    </div>
                    <div>
                      <span className="text-foreground-400">主演：</span>
                      <span className="text-foreground-700">{show.actors.join('、')}</span>
                    </div>
                    <div>
                      <span className="text-foreground-400">类型：</span>
                      <span className="text-foreground-700">{show.category}</span>
                    </div>
                    <div>
                      <span className="text-foreground-400">地区：</span>
                      <span className="text-foreground-700">{show.region}</span>
                    </div>
                  </div>

                  <Link
                    to={`/detail/${show.id}`}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-accent-500 text-white font-semibold text-sm rounded-xl hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-circle-fill text-xl"></i>
                    立即播放 第{currentEpNum}集
                  </Link>
                </div>
              </div>

              <div className="mt-10">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5">
                  剧集列表
                  <span className="text-sm font-normal text-foreground-400 ml-2">
                    共{totalEpisodes}集 · {show.status === 'completed' ? '已完结' : `更新至${show.currentEpisode}集`}
                  </span>
                </h2>
                <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-2 md:gap-3">
                  {episodeNumbers.map((ep) => (
                    <button
                      key={ep}
                      className={`px-2 py-2 text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                        ep <= show.currentEpisode
                          ? ep === currentEpNum
                            ? 'bg-accent-500 text-white font-medium'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                          : 'bg-background-100/50 text-foreground-300 cursor-not-allowed'
                      }`}
                      disabled={ep > show.currentEpisode}
                    >
                      第{ep}集
                    </button>
                  ))}
                </div>
              </div>

              {relatedShows.length > 0 && (
                <div className="mt-12 mb-10">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                      相关推荐
                    </h2>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4 md:gap-5">
                    {relatedShows.map((s) => (
                      <MovieCard key={s.id} show={s} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}